// SPDX-License-Identifier: LicenseRef-Proprietary
// Copyright (c) 2026 Invariant Holdings LLC
import { GENERATOR_CODES, isNeutralizationTheoremCode } from "@csk/core";
import { WASM_THEOREM_META } from "./meta.js";
const GENERATOR_NAMES = {
    G1: "Ambient Effect",
    G2: "Epistemic Lift",
    G3: "Time Reversal",
    G4: "Authority Duplication",
};
export function isGeneratorCode(value) {
    return GENERATOR_CODES.includes(value);
}
export function requireGeneratorCode(value) {
    const trimmed = value.trim();
    if (isGeneratorCode(trimmed))
        return trimmed;
    throw new Error(`Unknown square generator code: ${value}`);
}
export function requireTheoremCode(value) {
    if (isNeutralizationTheoremCode(value))
        return value;
    throw new Error(`Unknown theorem code: ${value}`);
}
function squareBoundary(theoremCode, seamOrientation, generatorCode) {
    return {
        top: `${theoremCode}:${generatorCode}:horizontal-source`,
        bottom: `${theoremCode}:${generatorCode}:horizontal-target`,
        left: `${seamOrientation}:${generatorCode}:vertical-source`,
        right: `${seamOrientation}:${generatorCode}:vertical-target`,
    };
}
export function squareDecisionFor(theoremCodeValue, generatorCodeValue) {
    const theoremCode = requireTheoremCode(theoremCodeValue);
    const generatorCode = requireGeneratorCode(generatorCodeValue);
    const meta = WASM_THEOREM_META[theoremCode];
    if (meta === undefined) {
        throw new Error(`Missing theorem metadata for ${theoremCode}`);
    }
    const status = meta.generator === generatorCode ? "forbidden" : "admitted";
    return {
        theoremCode,
        theoremName: meta.name,
        seamOrientation: meta.seamType,
        generatorCode,
        generatorName: GENERATOR_NAMES[generatorCode],
        squareId: `${theoremCode}.${meta.seamType}.${generatorCode}`,
        status,
        boundary: squareBoundary(theoremCode, meta.seamType, generatorCode),
        reason: status === "forbidden"
            ? `${meta.name} forbids ${generatorCode} at the ${meta.seamType} seam`
            : `${generatorCode} is not the forbidden generator for ${meta.name}`,
    };
}
export function squareIsForbidden(theoremCode, generatorCode) {
    return squareDecisionFor(theoremCode, generatorCode).status === "forbidden";
}
export function serializeSquareDecision(theoremCode, generatorCode) {
    return JSON.stringify(squareDecisionFor(theoremCode, generatorCode));
}
function boundaryMatches(actual, expected) {
    if (typeof actual !== "object" || actual === null)
        return false;
    const record = actual;
    return (record.top === expected.top &&
        record.bottom === expected.bottom &&
        record.left === expected.left &&
        record.right === expected.right);
}
export function parseWasmSquareDecision(serialized) {
    const value = JSON.parse(serialized);
    if (typeof value !== "object" || value === null) {
        throw new Error("WASM square decision must be an object");
    }
    const record = value;
    const expected = squareDecisionFor(String(record.theoremCode ?? ""), String(record.generatorCode ?? ""));
    if (record.theoremName !== expected.theoremName ||
        record.seamOrientation !== expected.seamOrientation ||
        record.generatorName !== expected.generatorName ||
        record.squareId !== expected.squareId ||
        record.status !== expected.status ||
        record.reason !== expected.reason ||
        !boundaryMatches(record.boundary, expected.boundary)) {
        throw new Error("WASM square decision does not match canonical metadata");
    }
    return expected;
}
//# sourceMappingURL=squares.js.map